import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { track } from "@/lib/analytics";
import { frasePreco } from "@/lib/preco";
import { usePlans } from "@/hooks/usePlans";

const SEEN_KEY = "tuto_exit_intent_seen";
// Imagem profissional (Unsplash, licença livre) — advogada com café.
const IMG = "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=900&q=80";

export default function ExitIntentModal() {
  const { precoDeEntrada } = usePlans();
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [imgError, setImgError] = useState(false);

  useEffect(() => {
    if (loading || user) return; // nunca com usuário logado
    if (sessionStorage.getItem(SEEN_KEY)) return;

    const onMouseOut = (e) => {
      // cursor saiu pela borda superior da viewport (intenção de fechar/trocar de aba)
      if (e.clientY <= 0 && !e.relatedTarget) {
        setOpen(true);
        track("exit_intent_shown");
        sessionStorage.setItem(SEEN_KEY, "1");
        document.removeEventListener("mouseout", onMouseOut);
      }
    };

    // pequeno atraso pra não disparar logo na chegada
    const t = setTimeout(() => document.addEventListener("mouseout", onMouseOut), 4000);
    return () => {
      clearTimeout(t);
      document.removeEventListener("mouseout", onMouseOut);
    };
  }, [user, loading]);

  if (!open) return null;

  const close = () => setOpen(false);

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/50 flex items-center justify-center p-4"
      onClick={close}
      role="dialog"
      aria-modal="true"
      aria-label="Oferta TUTO"
    >
      <div
        className="relative bg-white rounded-2xl overflow-hidden w-full max-w-md shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={close}
          aria-label="Fechar"
          className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-white/80 hover:bg-white flex items-center justify-center text-[#335480] shadow"
        >
          <X size={20} />
        </button>

        {/* Imagem (com fallback em gradiente caso não carregue) */}
        {imgError ? (
          <div className="h-48 hero-gradient" />
        ) : (
          <img
            src={IMG}
            alt="Advogada com mais tempo para a estratégia"
            className="w-full h-52 object-cover object-top"
            onError={() => setImgError(true)}
            loading="lazy"
          />
        )}

        <div className="bg-[#F4F7FB] px-8 py-8 text-center">
          <h3 className="text-2xl font-bold text-[#001533] mb-3" style={{ fontFamily: "Cabinet Grotesk" }}>
            Já pensou em dedicar mais tempo à sua advocacia?
          </h3>
          <p className="text-[#335480] text-base mb-6">
            O TUTO lê o DJEN e organiza suas intimações para você advogar com estratégia — sem perder publicação.
          </p>
          <Button
            data-testid="exit-intent-cta"
            onClick={() => { track("cta_click", { location: "exit_intent" }); track("signup_start", { location: "exit_intent" }); close(); navigate("/registro"); }}
            className="bg-[#003882] hover:bg-[#00285e] text-white font-bold text-base h-12 px-8"
          >
            Experimentar grátis
          </Button>
          <p className="text-[#335480] text-xs mt-3">
            {frasePreco("{preco}/mês • cancele quando quiser", precoDeEntrada(), "cancele quando quiser")}
          </p>
        </div>
      </div>
    </div>
  );
}
